/**
 * Tax Reports Page
 * 
 * Main page for managing Finnish Tax Authority forms (Y4, Y5, VAT 101/102).
 * Features:
 * - Form type selection (Y4/Y5/VAT101/VAT102)
 * - Fiscal period selection
 * - Preview generated forms
 * - Download PDF/XML
 * - History of generated reports
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrganization } from '../hooks/useOrganization';
import {
  taxReportService,
  type TaxReportType,
  type Y4FormData,
  type Y5FormData,
  type VatFormData,
} from '../api/services/TaxReportService';
import { Card, Button } from '../components';

interface FiscalYear {
  year: number;
  label: string;
}

interface VatPeriod {
  year: number;
  quarter?: number;
  month?: number;
  label: string;
}

export default function TaxReportsPage() {
  const { t } = useTranslation();
  const { currentOrganization } = useOrganization();
  
  // Form state
  const [selectedType, setSelectedType] = useState<TaxReportType>('y4');
  const [selectedFiscalYear, setSelectedFiscalYear] = useState<string>('');
  const [selectedVatPeriod, setSelectedVatPeriod] = useState<VatPeriod | null>(null);
  
  // Preview state
  const [previewData, setPreviewData] = useState<Y4FormData | Y5FormData | VatFormData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'generate' | 'preview' | 'history'>('generate');

  // Generate fiscal years (current year and 2 previous)
  const fiscalYears: FiscalYear[] = (() => {
    const current = new Date().getFullYear();
    return [
      { year: current, label: `${current}` },
      { year: current - 1, label: `${current - 1}` },
      { year: current - 2, label: `${current - 2}` },
    ];
  })();

  // Generate VAT periods
  const vatPeriods: VatPeriod[] = (() => {
    const periods: VatPeriod[] = [];
    const current = new Date().getFullYear();
    
    for (let year = current; year >= current - 1; year--) {
      // Quarters
      periods.push({ year, quarter: 1, label: `Q1 ${year}` });
      periods.push({ year, quarter: 2, label: `Q2 ${year}` });
      periods.push({ year, quarter: 3, label: `Q3 ${year}` });
      periods.push({ year, quarter: 4, label: `Q4 ${year}` });
      
      // Months for current year
      if (year === current) {
        const currentMonth = new Date().getMonth() + 1;
        for (let month = currentMonth; month >= 1; month--) {
          periods.push({
            year,
            month,
            label: `${month.toString().padStart(2, '0')}/${year}`,
          });
        }
      }
    }
    
    return periods;
  })();

  // Set default fiscal year on mount
  useEffect(() => {
    if (fiscalYears.length > 0 && !selectedFiscalYear) {
      setSelectedFiscalYear(fiscalYears[0].year.toString());
    }
  }, []);

  const handlePreview = async () => {
    if (!currentOrganization) {
      setError(t('taxReports.errors.noOrganization'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let data: Y4FormData | Y5FormData | VatFormData;

      switch (selectedType) {
        case 'y4':
          data = await taxReportService.previewY4(
            currentOrganization.id,
            selectedFiscalYear
          );
          break;
        case 'y5':
          data = await taxReportService.previewY5(
            currentOrganization.id,
            selectedFiscalYear
          );
          break;
        case 'vat-101':
          if (!selectedVatPeriod) {
            throw new Error(t('taxReports.errors.noPeriod'));
          }
          data = await taxReportService.previewVat101(
            currentOrganization.id,
            selectedVatPeriod.year,
            selectedVatPeriod.quarter,
            selectedVatPeriod.month
          );
          break;
        case 'vat-102':
          if (!selectedVatPeriod) {
            throw new Error(t('taxReports.errors.noPeriod'));
          }
          data = await taxReportService.previewVat102(
            currentOrganization.id,
            selectedVatPeriod.year,
            selectedVatPeriod.quarter,
            selectedVatPeriod.month
          );
          break;
        default:
          throw new Error('Unknown report type');
      }

      setPreviewData(data);
      setActiveTab('preview');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('taxReports.errors.previewFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (format: 'pdf' | 'xml') => {
    if (!currentOrganization) return;

    setLoading(true);
    setError(null);

    try {
      let blob: Blob;
      let filename: string;

      const orgId = currentOrganization.id;
      const businessId = 'unknown';

      switch (selectedType) {
        case 'y4':
          blob = await taxReportService.generateY4({
            organization_id: orgId,
            fiscal_year: selectedFiscalYear,
            format,
          });
          filename = `Y4_${businessId}_${selectedFiscalYear}.${format}`;
          break;
        case 'y5':
          blob = await taxReportService.generateY5({
            organization_id: orgId,
            fiscal_year: selectedFiscalYear,
            format,
          });
          filename = `Y5_${businessId}_${selectedFiscalYear}.${format}`;
          break;
        case 'vat-101':
          if (!selectedVatPeriod) return;
          blob = await taxReportService.generateVat101({
            organization_id: orgId,
            year: selectedVatPeriod.year,
            quarter: selectedVatPeriod.quarter,
            month: selectedVatPeriod.month,
            format,
          });
          const vat101Period = selectedVatPeriod.month 
            ? selectedVatPeriod.month.toString().padStart(2, '0')
            : selectedVatPeriod.quarter 
              ? `Q${selectedVatPeriod.quarter}`
              : '';
          filename = `VAT101_${businessId}_${selectedVatPeriod.year}${vat101Period}.${format}`;
          break;
        case 'vat-102':
          if (!selectedVatPeriod) return;
          blob = await taxReportService.generateVat102({
            organization_id: orgId,
            year: selectedVatPeriod.year,
            quarter: selectedVatPeriod.quarter,
            month: selectedVatPeriod.month,
            format,
          });
          const vat102Period = selectedVatPeriod.month 
            ? selectedVatPeriod.month.toString().padStart(2, '0')
            : selectedVatPeriod.quarter 
              ? `Q${selectedVatPeriod.quarter}`
              : '';
          filename = `VAT102_${businessId}_${selectedVatPeriod.year}${vat102Period}.${format}`;
          break;
        default:
          return;
      }

      taxReportService.downloadReport(blob, filename);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('taxReports.errors.downloadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const isVatType = selectedType === 'vat-101' || selectedType === 'vat-102';

  const getReportTypeLabel = (type: TaxReportType): string => {
    switch (type) {
      case 'y4':
        return t('taxReports.types.y4');
      case 'y5':
        return t('taxReports.types.y5');
      case 'vat-101':
        return t('taxReports.types.vat101');
      case 'vat-102':
        return t('taxReports.types.vat102');
      default:
        return type;
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">{t('taxReports.title')}</h1>
        <p className="text-gray-600 mt-1">{t('taxReports.subtitle')}</p>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
          <button
            onClick={() => setError(null)}
            className="ml-2 text-red-500 hover:text-red-700"
          >
            ×
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('generate')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'generate'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('taxReports.tabs.generate')}
          </button>
          {previewData && (
            <button
              onClick={() => setActiveTab('preview')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'preview'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('taxReports.tabs.preview')}
            </button>
          )}
          <button
            onClick={() => setActiveTab('history')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'history'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('taxReports.tabs.history')}
          </button>
        </nav>
      </div>

      {/* Generate Tab */}
      {activeTab === 'generate' && (
        <Card className="max-w-2xl">
          <h2 className="text-xl font-semibold text-gray-800 mb-6">
            {t('taxReports.generate.title')}
          </h2>

          <div className="space-y-6">
            {/* Report Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('taxReports.form.type')}
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(['y4', 'y5', 'vat-101', 'vat-102'] as TaxReportType[]).map((type) => (
                  <button
                    key={type}
                    onClick={() => {
                      setSelectedType(type);
                      setPreviewData(null);
                    }}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      selectedType === type
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-medium text-gray-900">
                      {getReportTypeLabel(type)}
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      {t(`taxReports.types.${type}Description`)}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Fiscal Year Selection (for Y4/Y5) */}
            {!isVatType && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('taxReports.form.fiscalYear')}
                </label>
                <select
                  value={selectedFiscalYear}
                  onChange={(e) => setSelectedFiscalYear(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {fiscalYears.map((fy) => (
                    <option key={fy.year} value={fy.year}>
                      {fy.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* VAT Period Selection */}
            {isVatType && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('taxReports.form.vatPeriod')}
                </label>
                <select
                  value={selectedVatPeriod ? JSON.stringify(selectedVatPeriod) : ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedVatPeriod(value ? JSON.parse(value) : null);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">{t('taxReports.form.selectPeriod')}</option>
                  {vatPeriods.map((period) => (
                    <option key={JSON.stringify(period)} value={JSON.stringify(period)}>
                      {period.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Preview Button */}
            <div className="pt-4">
              <Button
                onClick={handlePreview}
                disabled={loading || (isVatType && !selectedVatPeriod)}
                className="w-full"
              >
                {loading ? (
                  <>
                    <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                    {t('taxReports.form.generating')}
                  </>
                ) : (
                  t('taxReports.form.preview')
                )}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Preview Tab */}
      {activeTab === 'preview' && previewData && (
        <div className="space-y-6">
          <Card>
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-800">
                  {t('taxReports.preview.title')}
                </h2>
                <p className="text-gray-600">
                  {getReportTypeLabel(selectedType)} - {'fiscalPeriod' in previewData
                    ? previewData.fiscalPeriod.startDate
                    : 'reportingPeriod' in previewData
                      ? `${previewData.reportingPeriod.year}`
                      : ''}
                </p>
              </div>
              <div className="flex space-x-3">
                <Button
                  variant="secondary"
                  onClick={() => handleDownload('pdf')}
                  disabled={loading}
                >
                  {t('taxReports.download.pdf')}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => handleDownload('xml')}
                  disabled={loading}
                >
                  {t('taxReports.download.xml')}
                </Button>
              </div>
            </div>

            {/* Y4/Y5 Preview */}
            {('membershipFees' in previewData || 'maintenanceFees' in previewData) && (
              <div className="space-y-6">
                {/* Organization Info */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-medium text-gray-900 mb-2">
                    {t('taxReports.preview.organization')}
                  </h3>
                  <p className="text-sm text-gray-700">{previewData.organization.name}</p>
                  <p className="text-sm text-gray-500">{previewData.organization.businessId}</p>
                </div>

                {/* Income Statement */}
                <div>
                  <h3 className="font-medium text-gray-900 mb-3">
                    {t('taxReports.preview.incomeStatement')}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="bg-green-50 p-4 rounded-lg">
                      <p className="text-sm text-green-600">
                        {t('taxReports.preview.totalIncome')}
                      </p>
                      <p className="text-2xl font-bold text-green-700">
                        {previewData.incomeStatement.totalIncome.toFixed(2)} €
                      </p>
                    </div>
                    <div className="bg-red-50 p-4 rounded-lg">
                      <p className="text-sm text-red-600">
                        {t('taxReports.preview.totalExpenses')}
                      </p>
                      <p className="text-2xl font-bold text-red-700">
                        {previewData.incomeStatement.totalExpenses.toFixed(2)} €
                      </p>
                    </div>
                    <div className={`p-4 rounded-lg ${
                      previewData.incomeStatement.netResult >= 0 
                        ? 'bg-blue-50' 
                        : 'bg-orange-50'
                    }`}>
                      <p className={`text-sm ${
                        previewData.incomeStatement.netResult >= 0 
                          ? 'text-blue-600' 
                          : 'text-orange-600'
                      }`}>
                        {t('taxReports.preview.netResult')}
                      </p>
                      <p className={`text-2xl font-bold ${
                        previewData.incomeStatement.netResult >= 0 
                          ? 'text-blue-700' 
                          : 'text-orange-700'
                      }`}>
                        {previewData.incomeStatement.netResult.toFixed(2)} €
                      </p>
                    </div>
                  </div>
                </div>

                {/* Y4 Specific Details */}
                {'membershipFees' in previewData && (
                  <div>
                    <h3 className="font-medium text-gray-900 mb-3">
                      {t('taxReports.preview.y4Details')}
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      <div className="border p-3 rounded">
                        <p className="text-sm text-gray-600">{t('taxReports.preview.membershipFees')}</p>
                        <p className="font-medium">{previewData.membershipFees.toFixed(2)} €</p>
                      </div>
                      <div className="border p-3 rounded">
                        <p className="text-sm text-gray-600">{t('taxReports.preview.activityIncome')}</p>
                        <p className="font-medium">{previewData.activityIncome.toFixed(2)} €</p>
                      </div>
                      <div className="border p-3 rounded">
                        <p className="text-sm text-gray-600">{t('taxReports.preview.grantsReceived')}</p>
                        <p className="font-medium">{previewData.grantsReceived.toFixed(2)} €</p>
                      </div>
                      <div className="border p-3 rounded">
                        <p className="text-sm text-gray-600">{t('taxReports.preview.personnelCosts')}</p>
                        <p className="font-medium">{previewData.personnelCosts.toFixed(2)} €</p>
                      </div>
                      <div className="border p-3 rounded">
                        <p className="text-sm text-gray-600">{t('taxReports.preview.otherExpenses')}</p>
                        <p className="font-medium">{previewData.otherExpenses.toFixed(2)} €</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Y5 Specific Details */}
                {'maintenanceFees' in previewData && (
                  <div>
                    <h3 className="font-medium text-gray-900 mb-3">
                      {t('taxReports.preview.y5Details')}
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      <div className="border p-3 rounded">
                        <p className="text-sm text-gray-600">{t('taxReports.preview.maintenanceFees')}</p>
                        <p className="font-medium">{previewData.maintenanceFees.toFixed(2)} €</p>
                      </div>
                      <div className="border p-3 rounded">
                        <p className="text-sm text-gray-600">{t('taxReports.preview.repairCosts')}</p>
                        <p className="font-medium">{previewData.repairCosts.toFixed(2)} €</p>
                      </div>
                      <div className="border p-3 rounded">
                        <p className="text-sm text-gray-600">{t('taxReports.preview.heatingCosts')}</p>
                        <p className="font-medium">{previewData.heatingCosts.toFixed(2)} €</p>
                      </div>
                      <div className="border p-3 rounded">
                        <p className="text-sm text-gray-600">{t('taxReports.preview.electricityCosts')}</p>
                        <p className="font-medium">{previewData.electricityCosts.toFixed(2)} €</p>
                      </div>
                      <div className="border p-3 rounded">
                        <p className="text-sm text-gray-600">{t('taxReports.preview.adminCosts')}</p>
                        <p className="font-medium">{previewData.adminCosts.toFixed(2)} €</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* VAT Preview */}
            {'vatDetails' in previewData && (
              <div className="space-y-6">
                {/* Organization Info */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-medium text-gray-900 mb-2">
                    {t('taxReports.preview.organization')}
                  </h3>
                  <p className="text-sm text-gray-700">{previewData.organization.name}</p>
                  <p className="text-sm text-gray-500">{previewData.organization.businessId}</p>
                </div>

                {/* VAT Summary */}
                <div>
                  <h3 className="font-medium text-gray-900 mb-3">
                    {t('taxReports.preview.vatSummary')}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600">
                        {t('taxReports.preview.domesticSales')}
                      </p>
                      <p className="text-xl font-bold text-gray-900">
                        {previewData.vatDetails.domesticSales.toFixed(2)} €
                      </p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600">
                        {t('taxReports.preview.vatOnSales')}
                      </p>
                      <p className="text-xl font-bold text-red-600">
                        {previewData.vatDetails.domesticSalesVat.toFixed(2)} €
                      </p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600">
                        {t('taxReports.preview.deductibleVat')}
                      </p>
                      <p className="text-xl font-bold text-green-600">
                        {previewData.vatDetails.deductibleVat.toFixed(2)} €
                      </p>
                    </div>
                  </div>

                  {/* Net VAT */}
                  <div className={`p-4 rounded-lg ${
                    previewData.vatDetails.vatPayable >= 0 
                      ? 'bg-red-50' 
                      : 'bg-green-50'
                  }`}>
                    <p className={`text-sm font-medium ${
                      previewData.vatDetails.vatPayable >= 0 
                        ? 'text-red-700' 
                        : 'text-green-700'
                    }`}>
                      {previewData.vatDetails.vatPayable >= 0 
                        ? t('taxReports.preview.vatPayable')
                        : t('taxReports.preview.vatReceivable')}
                    </p>
                    <p className={`text-3xl font-bold ${
                      previewData.vatDetails.vatPayable >= 0 
                        ? 'text-red-700' 
                        : 'text-green-700'
                    }`}>
                      {Math.abs(previewData.vatDetails.vatPayable).toFixed(2)} €
                    </p>
                  </div>
                </div>
              </div>
            )}
          </Card>

          <div className="flex justify-between">
            <Button variant="secondary" onClick={() => setActiveTab('generate')}>
              ← {t('taxReports.backToGenerate')}
            </Button>
          </div>
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <Card>
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            {t('taxReports.history.title')}
          </h2>
          <p className="text-gray-500 text-center py-8">
            {t('taxReports.history.empty')}
          </p>
        </Card>
      )}
    </div>
  );
}
